import Button from "@/design-system/button";
import Carousel from "@/design-system/carousel";
import PulseLoading from "@/design-system/pulse-loading";
import type { ILogoTileWithCounter } from "@/src/brand/LogoTileWithCounter";
import { LogoTileWithCounter } from "@/src/brand/LogoTileWithCounter";
import { getUrlParameterByName } from "@/src/common/Helpers";
import type { IViewportWrapperRequiredProps } from "@/src/master/ViewportInjector";
import ViewportWrapper from "@/src/master/ViewportInjector";
import type { QuerySmMax } from "@/styles/tokens.stylex";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { TelemetryEntity, jumpToSectionId } from "@Components/Modules/Helpers";
import { Device } from "@Components/Modules/ViewportResize";
import { Panel } from "@Components/Panel";
import * as stylex from "@stylexjs/stylex";

import type { ILogoTile } from "../common/LogoTile";

export interface IBrandByCategory extends IComponentAjax<IBrandByCategoryResult> {
    selectedCategory: number;
    id: string;
    itemToUpdate: IBrandIdFollowResult;
    categoryName: string;
    title?: string;
    showMore: string;
    onItemChange?: (brand: IBrandIdFollowResult, result: boolean) => void;
    onDataLoad?: (response: IBrandByCategoryResult) => void;
    brandSearchName: string;
    itemsNumber: number;
    numberOfRows?: number;
    itemsNumberTablet: number;
    showAll?: boolean;
}

export interface IBrandIdFollowResult {
    id?: string;
    initialData: boolean;
}

export interface IBrandByCategoryState extends IComponentAjaxState {
    initialData?: IBrandByCategoryResult;
    numberOfRows: number;
}

export interface IBrandByCategoryResult {
    brands: ILogoTile[];
    page?: number;
}

class BrandsByCategoryBase extends ComponentAjax<
    IBrandByCategoryResult,
    IBrandByCategory & IViewportWrapperRequiredProps,
    IBrandByCategoryState
> {
    public static defaultProps: IBrandByCategory = {
        ...ComponentAjax.defaultProps,
        selectedCategory: -1,
        id: null,
        itemToUpdate: null,
        categoryName: "",
        showMore: null,
        brandSearchName: "",
        itemsNumber: 5,
        itemsNumberTablet: 3,
        showAll: false,
    };

    private static isMatch: (brandName: string, searchValue: string) => boolean;

    private brandsNamesUppercase: string[];

    private aggregatedResponse: IBrandByCategoryResult;

    constructor(props: IBrandByCategory & IViewportWrapperRequiredProps) {
        super(props);

        this.state = {
            initialData: props.initialData,
            isLoading: !props.initialData,
            numberOfRows: props.numberOfRows || props.numberOfRows === 0 ? props.numberOfRows : 1,
        };

        if (props.initialData && props.initialData.brands) {
            this.brandsNamesUppercase = props.initialData.brands.map((value) =>
                value.brandName.toLocaleUpperCase(),
            );
        }
    }

    public componentDidMount(): void {
        if (!this.props.initialData) {
            this.brandsNamesUppercase = [];
            this.fetchData(0);
        }

        if (BrandsByCategoryBase.isMatch) {
            return;
        }

        BrandsByCategoryBase.isMatch = (brandName: string, searchValue: string): boolean =>
            brandName.indexOf(searchValue) !== -1;
    }

    public UNSAFE_componentWillReceiveProps(nextProps: IBrandByCategory): void {
        let { initialData } = nextProps;
        if (!initialData) {
            return;
        }
        if (
            (nextProps.itemToUpdate && !this.props.itemToUpdate) ||
            (nextProps.itemToUpdate &&
                (this.props.itemToUpdate.id !== nextProps.itemToUpdate.id ||
                    this.props.itemToUpdate.initialData !== nextProps.itemToUpdate.initialData))
        ) {
            this.brandsNamesUppercase = [];

            initialData = {
                brands: nextProps.initialData.brands.map((currentValue) => {
                    this.brandsNamesUppercase = [
                        ...this.brandsNamesUppercase,
                        currentValue.brandName.toLocaleUpperCase(),
                    ];

                    return currentValue;
                }),
            };
        }

        if (
            (initialData && this.props.initialData && this.props.initialData !== initialData) ||
            !this.props.initialData
        ) {
            this.setState({
                initialData,
                isLoading: false,
            });
        }
    }

    public componentDidUpdate(
        _: Readonly<IBrandByCategory & IViewportWrapperRequiredProps>,
        prevState: Readonly<IBrandByCategoryState>,
    ): void {
        if (!prevState.initialData && this.state.initialData?.brands) {
            if (getUrlParameterByName("position") === this.props.id) {
                jumpToSectionId(this.props.id, false, 20);
            }
        }
    }

    public render(): React.ReactNode {
        const tiles = this.renderBrandsTiles();

        if (!tiles || tiles.length === 0) {
            return null;
        }

        return (
            <Panel title={this.props.title || this.props.categoryName} clientId={this.props.clientId}>
                {this.renderBody(tiles)}
                {this.renderAjaxErrorContainer()}
                {this.state.initialData && tiles.length > this.itemsToShow ? (
                    <Button
                        as="button"
                        styleX={styles.button}
                        variant="transparent"
                        size="M"
                        onClick={this.showMore}
                    >
                        {this.props.showMore}
                    </Button>
                ) : (
                    this.props.device !== Device.Mobile && <PulseLoading />
                )}
            </Panel>
        );
    }

    protected ajaxSuccess(response: IBrandByCategoryResult): void {
        if (!response || !response.brands) {
            this.setState({ isLoading: false });
            return;
        }

        const hasMoreBrands = !!response.page;

        const brands = this.aggregatedResponse ? [...this.aggregatedResponse.brands] : [];

        this.aggregatedResponse = {
            ...response,
            brands: [...brands, ...response.brands],
        };

        if (hasMoreBrands) {
            this.fetchData(response.page);
            return;
        }

        if (this.props.onDataLoad) {
            this.props.onDataLoad.call(this, this.aggregatedResponse);
        }

        this.setState((cState) => ({
            ...cState,
            initialData: this.aggregatedResponse,
            isLoading: false,
        }));
    }

    protected ajaxFail(): boolean {
        this.setState({ isLoading: false });
        return true;
    }

    private fetchData(page?: number): void {
        if (this.state.initialData && !this.state.initialData.page) {
            return;
        }

        if (!this.state.isLoading) {
            this.setState({ isLoading: true });
        }

        this.ajaxRequest({ categoryName: encodeURIComponent(this.props.categoryName), page });
    }

    private renderBrandsTiles(): JSX.Element[] {
        let brands = this.state.initialData?.brands;

        if (!brands || brands.length === 0) {
            if (this.state.isLoading) {
                brands = Array.from<ILogoTile>({ length: 5 }).map(
                    (_, index) => ({ id: `${index}` }) as ILogoTile,
                );
            } else {
                return [];
            }
        }

        let brandsToRender: JSX.Element[] = [];

        let index: number = 0;
        let numberOfTiles: number = 0;

        const { length } = brands;
        const { itemsToShow } = this;

        const searchValue = this.props.brandSearchName.toLocaleUpperCase();
        const isLazyLoading = !searchValue;

        while (index < length && numberOfTiles < itemsToShow) {
            if (
                isLazyLoading ||
                BrandsByCategoryBase.isMatch(this.brandsNamesUppercase[index], searchValue)
            ) {
                const currentValue = brands[index] as ILogoTileWithCounter;
                if (currentValue.logo) {
                    currentValue.logo.lazyLoading = isLazyLoading;
                }

                brandsToRender = [
                    ...brandsToRender,
                    <LogoTileWithCounter
                        key={currentValue.id}
                        {...currentValue}
                        telemetryCustom={{
                            eventName: `${TelemetryEntity.Brand}-BrandView`,
                            actionType: "View",
                            itemId: currentValue.id,
                        }}
                        type="brands"
                    />,
                ];
                numberOfTiles += 1;
            }
            index += 1;
        }

        if (index === numberOfTiles && index < length) {
            brandsToRender = [...brandsToRender, null];
        }

        return brandsToRender;
    }

    private showMore = (): boolean => {
        this.setState((prevState: IBrandByCategoryState) => ({
            numberOfRows: prevState.numberOfRows + 2,
        }));

        return true;
    };

    private get itemsInARow(): number {
        if (this.props.showAll === true) {
            return 9999;
        }

        if (this.props.device === Device.Tablet) {
            return this.props.itemsNumberTablet;
        }
        return this.props.device === Device.Mobile ? 9999 : this.props.itemsNumber;
    }

    private get itemsToShow(): number {
        return this.itemsInARow * this.state.numberOfRows;
    }

    private renderBody(brandsList: JSX.Element[]): JSX.Element {
        if (this.props.device === Device.Mobile) {
            return (
                <Carousel
                    showArrows={false}
                    styleX={styles.carousel}
                    itemStyleX={styles.item}
                    itemsToShow={2.5}
                >
                    {brandsList}
                </Carousel>
            );
        }

        return <div {...stylex.props(styles.wrapper)}>{brandsList}</div>;
    }
}

const BrandsByCategory = (props: IBrandByCategory) => (
    <ViewportWrapper Component={BrandsByCategoryBase} props={props} />
);

export { BrandsByCategoryBase, BrandsByCategory };

const smMax: QuerySmMax = "@media (max-width: 989px)";

const styles = stylex.create({
    carousel: {
        gap: 0,
        marginBlock: "0",
        marginInline: "-10px",
        width: "auto",
    },

    item: {
        paddingBlock: "0",
        paddingInline: "10px",
    },
    wrapper: {
        gap: "1rem",
        display: "grid",
        gridTemplateColumns: {
            default: "repeat(5, minmax(100px, 1fr))",
            [smMax]: "repeat(3, minmax(100px, 1fr))",
        },
    },
    button: {
        color: "#07c",
        display: "block",

        textTransform: "uppercase",
        marginBottom: 0,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 45,
    },
});
